import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import CTAButton from '@/components/shared/CTAButton'
import UtmLink from '@/components/shared/UtmLink'
import { lendersData, vehicleMakesData } from '@/shared/navigationData'

const MobileNavPopout = ({ isOpen, onClose }) => {
  const [expandedSection, setExpandedSection] = useState(null)
  const { pathname } = useLocation()

  // Reset accordion state each time the popout closes
  useEffect(() => {
    if (!isOpen) {
      setExpandedSection(null)
    }
  }, [isOpen])

  if (!isOpen) return null

  const toggleSection = section => {
    setExpandedSection(expandedSection === section ? null : section)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <button
        onClick={onClose}
        className="absolute top-6 right-6 z-60 transform rounded-lg bg-[#095678] p-2 shadow-lg transition-all duration-200 ease-in-out hover:scale-105 hover:bg-[#074460] hover:shadow-xl"
        aria-label="Close menu"
      >
        <X className="h-5 w-5 stroke-2 text-white" />
      </button>
      <div className="relative max-h-[80vh] w-full max-w-md overflow-y-auto rounded-lg bg-white shadow-2xl">
        <div className="p-6">
          <div className="space-y-4">
            <MobileNavSection
              title="Lenders"
              items={lendersData}
              isExpanded={expandedSection === 'lenders'}
              onToggle={() => toggleSection('lenders')}
              onClose={onClose}
              pathname={pathname}
            />
            <MobileNavSection
              title="Vehicles"
              items={vehicleMakesData}
              isExpanded={expandedSection === 'vehicles'}
              onToggle={() => toggleSection('vehicles')}
              onClose={onClose}
              pathname={pathname}
            />
            <MobileNavLink
              to="/about-us"
              onClick={onClose}
              active={pathname === '/about-us'}
            >
              About Us
            </MobileNavLink>
            <MobileNavLink
              to="/contact"
              onClick={onClose}
              active={pathname === '/contact'}
            >
              Contact Us
            </MobileNavLink>
            <div className="pt-4">
              <CTAButton className="w-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const MobileNavSection = ({
  title,
  items,
  isExpanded,
  onToggle,
  onClose,
  pathname,
}) => {
  return (
    <div className="border-b border-gray-200 pb-4">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between py-3 text-left text-lg font-semibold transition-colors hover:text-[#095678] focus:outline-none focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-[#095678]"
      >
        {title}
        {isExpanded ? (
          <ChevronUp className="h-5 w-5" />
        ) : (
          <ChevronDown className="h-5 w-5" />
        )}
      </button>
      {isExpanded && (
        <div className="mt-2 space-y-2 pl-4">
          {items.map(item => {
            const active = pathname === item.href
            return (
              <UtmLink
                key={item.title}
                to={item.href}
                onClick={onClose}
                aria-current={active ? 'page' : undefined}
                className={`block rounded-md py-2 text-sm transition-colors hover:text-[#095678] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#095678] ${active ? 'font-bold text-[#095678]' : ''}`}
              >
                {item.title}
              </UtmLink>
            )
          })}
        </div>
      )}
    </div>
  )
}

const MobileNavLink = ({ to, children, onClick, active }) => {
  return (
    <UtmLink
      to={to}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`block border-b border-gray-200 py-3 text-lg font-semibold transition-colors hover:text-[#095678] focus:outline-none focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-[#095678] ${active ? 'text-[#095678] underline underline-offset-4' : ''}`}
    >
      {children}
    </UtmLink>
  )
}

export default MobileNavPopout
